import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Loading, Typography, colors } from '@leapeasy/ui-kit';

const STATE_PRESETS = {
  noData: {
    title: 'No data available',
    description: '',
  },
  noResults: {
    title: 'No results found',
    description: 'Try adjusting or clearing your filters.',
  },
  apiError: {
    title: 'Request failed',
    description: 'We were unable to load this data. Please try again.',
  },
  accessDenied: {
    title: 'Access denied',
    description: 'You do not have permission to view this page.',
  },
  expiredInvite: {
    title: 'Invite link expired',
    description: 'This invite link is no longer valid. Request a new invitation.',
  },
  actionSuccess: {
    title: 'Action successful',
    description: 'Your changes have been saved.',
  },
  actionFailed: {
    title: 'Action failed',
    description: 'We could not complete your request. Please try again.',
  },
  exportFailed: {
    title: 'Export failed',
    description: 'Your file could not be generated. Please try again.',
  },
  slowLoading: {
    title: 'Still loading',
    description: 'This is taking longer than expected.',
  },
  notFound: {
    title: 'Page not found',
    description: 'The page you are looking for does not exist or may have been moved.',
  },
};

export const GlobalUXState = ({
  variant,
  title,
  description,
  compact,
  showLoadingSpinner,
  actionLabel,
  onAction,
}) => {
  const preset = STATE_PRESETS[variant] || {};
  const resolvedTitle = title || preset.title || '';
  const resolvedDescription = description || preset.description || '';

  return (
    <Container $compact={compact}>
      {showLoadingSpinner ? (
        <SpinnerContainer>
          <Loading size="small" isPathVisible />
        </SpinnerContainer>
      ) : null}
      {resolvedTitle ? (
        <Typography variant="body2" style={{ color: colors.black[900], fontWeight: 600 }}>
          {resolvedTitle}
        </Typography>
      ) : null}
      {resolvedDescription ? (
        <Typography variant="body3" style={{ color: colors.neutral[900] }}>
          {resolvedDescription}
        </Typography>
      ) : null}
      {actionLabel && typeof onAction === 'function' ? (
        <Button variant="tertiary" size="small" onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </Container>
  );
};

GlobalUXState.propTypes = {
  variant: PropTypes.oneOf([
    'noData',
    'noResults',
    'apiError',
    'accessDenied',
    'expiredInvite',
    'actionSuccess',
    'actionFailed',
    'exportFailed',
    'slowLoading',
    'notFound',
  ]),
  title: PropTypes.string,
  description: PropTypes.string,
  compact: PropTypes.bool,
  showLoadingSpinner: PropTypes.bool,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
};

GlobalUXState.defaultProps = {
  variant: 'noData',
  title: '',
  description: '',
  compact: false,
  showLoadingSpinner: false,
  actionLabel: '',
  onAction: undefined,
};

const Container = styled.div`
  width: 100%;
  max-width: ${({ $compact }) => ($compact ? '380px' : '480px')};
  margin: 0 auto;
  text-align: center;
  padding: ${({ $compact }) => ($compact ? '8px 0' : '20px 0')};
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`;

const SpinnerContainer = styled.div`
  margin-bottom: 4px;
`;
